import type { FC, ReactNode } from 'react'

import { createContext, useContext, useEffect, useState } from 'react'

export type Desk95Palette = {
    desktop: string
    face: string
    highlight: string
    faceLight: string
    shadow: string
    dark: string
    text: string
    disabledText: string
    titleActive: string
    titleActiveText: string
    titleInactive: string
    titleInactiveText: string
    surface: string
    surfaceText: string
    page: string
    pageText: string
    led: string
    ledFace: string
    selection: string
    selectionText: string
    hairline: string
    sectionBar: string
    sectionBarText: string
}

export const DESK95_LIGHT: Desk95Palette = {
    desktop: '#009688',
    face: '#c0c0c0',
    highlight: '#ffffff',
    faceLight: '#dfdfdf',
    shadow: '#808080',
    dark: '#000000',
    text: '#000000',
    disabledText: '#808080',
    titleActive: '#000080',
    titleActiveText: '#ffffff',
    titleInactive: '#808080',
    titleInactiveText: '#d4d4d4',
    surface: '#ffffff',
    surfaceText: '#000000',
    page: '#f4ecd8',
    pageText: '#141414',
    led: '#33ff66',
    ledFace: '#001a06',
    selection: '#000080',
    selectionText: '#ffffff',
    hairline: '#808080',
    sectionBar: '#000080',
    sectionBarText: '#ffffff'
}

export const DESK95_DARK: Desk95Palette = {
    desktop: '#06100f',
    face: '#3a3a3a',
    highlight: '#5e5e5e',
    faceLight: '#4c4c4c',
    shadow: '#1b1b1b',
    dark: '#000000',
    text: '#e8e8e8',
    disabledText: '#9a9a9a',
    titleActive: '#202058',
    titleActiveText: '#ffffff',
    titleInactive: '#2a2a2a',
    titleInactiveText: '#8a8a8a',
    surface: '#161616',
    surfaceText: '#e2e2e2',
    page: '#1c1c1c',
    pageText: '#e2e2e2',
    led: '#33ff66',
    ledFace: '#001a06',
    selection: '#26268f',
    selectionText: '#ffffff',
    hairline: '#1b1b1b',
    sectionBar: '#2c2c70',
    sectionBarText: '#ffffff'
}

/**
 * The four bevel edge colours generate every raised / sunken box-shadow in the
 * UI, so the 3D chrome stays coherent in either palette.
 */
export const raisedShadow = (palette: Desk95Palette, thin = false): string => {
    if (thin) {
        return `inset 1px 1px 0 0 ${palette.highlight}, inset -1px -1px 0 0 ${palette.shadow}`
    }

    return [
        `inset 1px 1px 0 0 ${palette.highlight}`,
        `inset -1px -1px 0 0 ${palette.dark}`,
        `inset 2px 2px 0 0 ${palette.faceLight}`,
        `inset -2px -2px 0 0 ${palette.shadow}`
    ].join(', ')
}

export const sunkenShadow = (palette: Desk95Palette, thin = false): string => {
    if (thin) {
        return `inset 1px 1px 0 0 ${palette.shadow}, inset -1px -1px 0 0 ${palette.highlight}`
    }

    return [
        `inset 1px 1px 0 0 ${palette.shadow}`,
        `inset -1px -1px 0 0 ${palette.highlight}`,
        `inset 2px 2px 0 0 ${palette.dark}`,
        `inset -2px -2px 0 0 ${palette.faceLight}`
    ].join(', ')
}

const Desk95ThemeContext = createContext<Desk95Palette>(DESK95_DARK)

export const useDesk95Theme = (): Desk95Palette => useContext(Desk95ThemeContext)

/**
 * Observes the site's global `<html data-theme>` toggle so the whole desktop
 * swaps palettes with the rest of the site. The desktop is dark by default and
 * only lifts to the classic grey-and-teal palette when light is asked for.
 */
export const useIsDark = (): boolean => {
    const [isDark, setIsDark] = useState(true)

    useEffect(() => {
        const root = document.documentElement

        const read = () => {
            const attr = root.getAttribute('data-theme')

            setIsDark(!(attr === 'light' || (attr === null && root.classList.contains('light'))))
        }

        read()

        const observer = new MutationObserver(read)

        observer.observe(root, { attributes: true, attributeFilter: ['data-theme', 'class'] })

        return () => observer.disconnect()
    }, [])

    return isDark
}

export const Desk95ThemeProvider: FC<{ children: ReactNode }> = ({ children }): ReactNode => {
    const isDark = useIsDark()

    return (
        <Desk95ThemeContext.Provider value={isDark ? DESK95_DARK : DESK95_LIGHT}>
            {children}
        </Desk95ThemeContext.Provider>
    )
}
